import { Component, Host, Prop, Element, State, h, Watch, Method } from '@stencil/core';
import { arrow_left, arrow_right } from 'pn-design-assets/pn-assets/icons.js';

interface CarouselData {
  slides: string[];
}

interface EventListenerConfig {
  element: HTMLElement;
  event: string;
  handler: EventListener;
}

@Component({
  tag: 'pn-marketweb-carousel',
  styleUrl: 'pn-marketweb-carousel.scss',
})
export class PnMarketwebCarousel {
  private static readonly DEFAULTS = {
    SWIPE_THRESHOLD_PX: 50,
    ANIMATION_DURATION_MS: 300,
    OVERSCROLL_LIMIT_PERCENT: 30,
  } as const;

  @Element() private hostElement!: HTMLElement;

  @Prop() previousButtonLabel!: string;
  @Prop() nextButtonLabel!: string;
  @Prop() ariaNavigationLabel!: string;
  @Prop() source!: string;
  @Prop() slidesToShow: number = 1;
  @Prop() showCarouselButtons: boolean = true;
  @Prop() showFirstSlidePrevButton: boolean = false;
  @Prop() showLastSlideNextButton: boolean = false;

  @State() private slidesData: string[] = [];
  @State() private currentSlide: number = 0;
  @State() private isDragging: boolean = false;
  @State() private slideWidth: number = 0;

  private slides: NodeListOf<HTMLElement> | null = null;
  private slidesContainer: HTMLElement | null = null;
  private totalSlides: number = 0;
  private prevButton: HTMLElement | null = null;
  private nextButton: HTMLElement | null = null;
  private dragStartX: number = 0;
  private listeners: EventListenerConfig[] = [];
  private memoizedSlideWidth: number | null = null;

  componentWillLoad(): void {
    this.setInitialValues();
  }

  componentDidLoad(): void {
    this.setupCarousel();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback(): void {
    this.cleanup();
    window.removeEventListener('resize', this.handleResize);
  }

  @Method()
  async goToSlide(index: number): Promise<void> {
    if (index >= 0 && index < this.totalSlides) {
      this.currentSlide = index;
      this.moveSlides();
      this.updateNavButtons();
    }
  }

  @Method()
  async getCurrentSlide(): Promise<number> {
    return this.currentSlide;
  }

  @Watch('source')
  handleSourceChange(newValue: string): void {
    if (newValue) {
      this.setInitialValues();
    }
  }

  private setInitialValues(): void {
    if (!this.source?.trim()) {
      console.warn('No source provided for carousel');
      return;
    }

    try {
      const parsedData = JSON.parse(this.source) as CarouselData;

      if (!parsedData?.slides) {
        throw new Error('Invalid slides data structure');
      }

      if (Array.isArray(parsedData.slides)) {
        this.slidesData = parsedData.slides;
        this.showCarouselButtons = this.slidesData.length > 1;
      } else {
        throw new Error('Slides data is not an array');
      }
    } catch (error) {
      console.error('Error parsing carousel source:', error instanceof Error ? error.message : 'Unknown error');
      this.slidesData = [];
    }
  }

  private setupCarousel(): void {
    this.initializeElements();
    this.attachEventListeners();
    this.updateNavButtons();
  }

  private initializeElements(): void {
    this.slidesContainer = this.hostElement.querySelector<HTMLElement>('[data-slides-container]');
    this.slides = this.hostElement.querySelectorAll<HTMLElement>('[data-slide]');
    this.totalSlides = this.slides?.length ?? 0;

    if (this.showCarouselButtons) {
      this.prevButton = this.hostElement.querySelector<HTMLElement>('[data-nav-button-prev]');
      this.nextButton = this.hostElement.querySelector<HTMLElement>('[data-nav-button-next]');
    }

    this.slideWidth = 100 / this.slidesToShow;
  }

  private attachEventListeners(): void {
    if (!this.slidesContainer) return;

    const addListener = (element: HTMLElement, event: string, handler: EventListener, options?: AddEventListenerOptions) => {
      element.addEventListener(event, handler, options);
      this.listeners.push({ element, event, handler });
    };

    // Touch events
    addListener(this.slidesContainer, 'touchstart', this.handleTouchStart as EventListener, { passive: true });
    addListener(this.slidesContainer, 'touchmove', this.handleTouchMove as EventListener, { passive: false });
    addListener(this.slidesContainer, 'touchend', this.handleTouchEnd as EventListener, { passive: true });

    // Mouse events
    addListener(this.slidesContainer, 'mousedown', this.handleTouchStart as EventListener);
    addListener(this.slidesContainer, 'mousemove', this.handleTouchMove as EventListener);
    addListener(this.slidesContainer, 'mouseup', this.handleTouchEnd as EventListener);
    addListener(this.slidesContainer, 'mouseleave', this.handleTouchEnd as EventListener);

    if (this.showCarouselButtons) {
      this.nextButton && addListener(this.nextButton, 'click', this.handleNextClick);
      this.prevButton && addListener(this.prevButton, 'click', this.handlePrevClick);
    }
  }

  private cleanup(): void {
    this.listeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.listeners = [];
  }

  private handleResize = (): void => {
    this.memoizedSlideWidth = null;
  };

  private getSlideWidth(): number {
    if (!this.memoizedSlideWidth && this.slidesContainer) {
      this.memoizedSlideWidth = this.slidesContainer.offsetWidth;
    }
    return this.memoizedSlideWidth ?? 0;
  }

  private handleNextClick = (e: Event): void => {
    e.stopPropagation();
    this.nextSlide();
  };

  private handlePrevClick = (e: Event): void => {
    e.stopPropagation();
    this.prevSlide();
  };

  private handleTouchStart = (e: TouchEvent | MouseEvent): void => {
    if ((e.target as HTMLElement).classList.contains('nav-button')) return;

    this.isDragging = true;
    this.dragStartX = 'touches' in e ? e.touches[0].clientX : e.clientX;
  };

  private handleTouchMove = (e: TouchEvent | MouseEvent): void => {
    if (!this.isDragging) return;

    if ('touches' in e) {
      e.preventDefault();
    }

    const currentPosition = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = currentPosition - this.dragStartX;
    const percentMove = (diff / this.getSlideWidth()) * 100;

    this.moveSlides(percentMove);
  };

  private handleTouchEnd = (e: TouchEvent | MouseEvent): void => {
    if (!this.isDragging) return;

    this.isDragging = false;

    const currentPosition = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
    const diff = currentPosition - this.dragStartX;

    if (Math.abs(diff) > PnMarketwebCarousel.DEFAULTS.SWIPE_THRESHOLD_PX) {
      if (diff > 0 && this.currentSlide > 0) {
        this.currentSlide--;
      } else if (diff < 0 && this.currentSlide < this.totalSlides - 1) {
        this.currentSlide++;
      }
    }

    this.moveSlides();
    this.updateNavButtons();
  };

  private calculateProportionalTranslation(): number {
    // Calculate proportional translation based on slidesToShow
    return -200 + (this.slidesToShow - 1) * 100;
  }

  private moveSlides(extraTranslate: number = 0): void {
    if (!this.slides || !this.slidesContainer) {
      return;
    }

    try {
      this.slides.forEach((slide, index) => {
        if (slide) {
          // Set each slide's width
          slide.style.width = `${this.slideWidth}%`;

          let translation = 0;

          if (this.slidesToShow === 1) {
            // Full slide view
            translation = -100 * this.currentSlide;
          } else {
            // Partial slide view
            if (this.totalSlides === 2) {
              // Special case for 2 slides
              if (this.currentSlide === 1) {
                translation = -50; // Both slides move by -50%
              }
            } else {
              // 3 or more slides
              if (this.currentSlide === 1) {
                // Second slide showing - all slides move -100%
                translation = -100;
              } else if (this.currentSlide === 2) {
                // Third slide showing
                if (index === 0) {
                  translation = -100; // First slide stays at -100%
                } else {
                  // Calculate proportional translation
                  translation = this.calculateProportionalTranslation();
                }
              }
            }
          }

          // Add any extra translation from dragging
          translation += extraTranslate;

          slide.style.transform = `translateX(${translation}%)`;
          slide.style.position = 'relative';

          // Update visibility for screen readers
          let isVisible;

          if (this.slidesToShow === 1) {
            isVisible = index === this.currentSlide;
          } else {
            isVisible =
              this.currentSlide === 0 || // First slide showing
              this.totalSlides === 2 || // Two slides - both visible
              (this.currentSlide === 1 && translation === -100) || // Second slide showing
              (this.currentSlide === 2 &&
                ((index === 0 && translation === -100) || // First slide still visible
                  index > 0)); // Second and third slides visible
          }

          slide.setAttribute('aria-hidden', (!isVisible).toString());
        }
      });
    } catch (error) {
      console.error('Error moving slides:', error);
    }
  }

  private updateNavButtons(): void {
    if (!this.showCarouselButtons) return;

    const isFirstSlide = this.currentSlide === 0;
    const isLastSlide = this.currentSlide === this.totalSlides - 1;

    if (this.prevButton) {
      this.prevButton.classList.toggle('invisible', isFirstSlide && !this.showFirstSlidePrevButton);
      this.prevButton.dataset.show = isFirstSlide ? `${this.showFirstSlidePrevButton}` : 'true';
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle('invisible', isLastSlide && !this.showLastSlideNextButton);
      this.nextButton.dataset.show = isLastSlide ? `${this.showLastSlideNextButton}` : 'true';
    }
  }

  private nextSlide(): void {
    if (this.currentSlide >= this.totalSlides - 1) {
      this.currentSlide = 0;
    } else {
      this.currentSlide++;
    }
    this.moveSlides();
    this.updateNavButtons();
  }

  private prevSlide(): void {
    if (this.currentSlide <= 0) {
      this.currentSlide = this.totalSlides - 1;
    } else {
      this.currentSlide--;
    }
    this.moveSlides();
    this.updateNavButtons();
  }

  render() {
    return (
      <Host>
        <div class="pn-marketweb-carousel">
          <section class="slider-wrapper">
            {this.showCarouselButtons && (
              <div class="nav-buttons" role="group" aria-label={this.ariaNavigationLabel}>
                <pn-button
                  appearance="light"
                  icon={arrow_left}
                  icon-only="true"
                  tooltip={this.previousButtonLabel}
                  class={`nav-button ${this.currentSlide === 0 && !this.showFirstSlidePrevButton ? 'invisible' : ''}`}
                  data-nav-button-prev
                  area-label={this.previousButtonLabel}
                  aria-disabled={this.currentSlide === 0 && !this.showFirstSlidePrevButton}>
                </pn-button>
                <pn-button
                  appearance="light"
                  icon={arrow_right}
                  icon-only="true"
                  tooltip={this.nextButtonLabel}
                  class="nav-button"
                  data-nav-button-next
                  area-label={this.nextButtonLabel}
                  aria-disabled={this.currentSlide === this.totalSlides - 1 && !this.showLastSlideNextButton}>
                </pn-button>
              </div>
            )}
            {this.slidesData && (
              <ul
                class="slides-container"
                data-slides-container
                data-partial={this.slidesToShow === 1 ? 'false' : 'true'}
                aria-live="polite"
                style={{ display: 'flex', transform: 'none' }}
              >
                {this.slidesData.map((slide, index) => (
                  <li class="slide" data-slide innerHTML={slide} style={{ width: this.slideWidth + '%' }} aria-hidden={index !== this.currentSlide}></li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </Host>
    );
  }
}
